import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { ArrowLeft, Users2, TrendingUp, Award, Clock } from "lucide-react";
import { Card } from "../components/ui/card";

export default function TherapistDashboard() {
  const { userId, signOut } = useSession();
  const navigate = useNavigate();
  const [therapist, setTherapist] = useState(null);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!userId) { navigate("/"); return; }
    api.getUser(userId).then(setTherapist).catch(() => {});
    api.listStudents({ therapist_id: userId }).then(async (list) => {
      setStudents(list);
      const all = {};
      for (const s of list) {
        all[s.user.id] = await api.progressSummary(s.user.id);
      }
      setStats(all);
    });
  }, [userId]);

  const totalSessions = Object.values(stats).reduce((a, b) => a + (b.total_sessions || 0), 0);
  const avgAcc = Object.values(stats).length
    ? Math.round(Object.values(stats).reduce((a, b) => a + (b.average_accuracy || 0), 0) / Object.values(stats).length)
    : 0;
  const totalStars = Object.values(stats).reduce((a, b) => a + (b.total_stars || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => { signOut(); navigate("/"); }} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="exit-therapist">
            <ArrowLeft className="h-4 w-4" /> Exit
          </button>
        </div>
        {therapist && (
          <div className="flex items-center gap-3">
            <img src={therapist.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div className="text-right">
              <div className="font-semibold text-sm">{therapist.name}</div>
              <div className="text-xs text-muted-foreground">Therapist</div>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Therapist control room</h1>
          <p className="text-muted-foreground mt-1">Monitor your caseload, assign exercises, and generate reports.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="therapist-stats">
          <Stat icon={Users2} label="Active students" value={students.length} accent="text-sky-600 bg-sky-50" />
          <Stat icon={Clock} label="Total sessions" value={totalSessions} accent="text-violet-600 bg-violet-50" />
          <Stat icon={TrendingUp} label="Avg. accuracy" value={`${avgAcc}%`} accent="text-emerald-600 bg-emerald-50" />
          <Stat icon={Award} label="Stars earned" value={totalStars} accent="text-orange-600 bg-orange-50" />
        </div>

        <section>
          <h2 className="text-xl font-bold mb-4">Your students</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="therapist-students">
            {students.map(s => {
              const sm = stats[s.user.id] || {};
              return (
                <button
                  key={s.user.id}
                  data-testid={`therapist-student-${s.user.id}`}
                  onClick={() => navigate(`/therapist/student/${s.user.id}`)}
                  className="text-left bg-white rounded-xl border border-border p-5 hover:border-sky-500 hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img src={s.user.avatar} alt="" className="h-12 w-12 rounded-xl object-cover bg-slate-100" />
                    <div>
                      <div className="font-bold">{s.user.name}</div>
                      <div className="text-xs text-muted-foreground">Age {s.profile.age} · {s.profile.color_theme}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Mini label="Sessions" value={sm.total_sessions || 0} />
                    <Mini label="Avg %" value={`${sm.average_accuracy || 0}`} />
                    <Mini label="Stars" value={sm.total_stars || 0} />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5 bg-white border border-border">
      <div className={`h-10 w-10 rounded-lg grid place-items-center mb-3 ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </Card>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg py-2">
      <div className="font-bold text-lg">{value}</div>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
    </div>
  );
}
